<template>
  <div class="relative isolate">
    <!--
      Single root element on purpose: App.vue wraps pages in
      <Transition mode="out-in">, which cannot track the leave of a fragment
      root. A template comment placed BEFORE this div made the root a fragment
      in dev builds (comments are kept there), and leaving the page stalled the
      router view on an empty <main>. `relative isolate` kept the page above
      App.vue's decorative blobs, which were removed on 2026-09-30.
    -->
    <div class="mx-auto max-w-4xl space-y-8 p-6">
      <!-- Header -->
      <header class="text-center">
        <h1 class="mb-4 text-4xl font-bold text-theme-fg">
          Documentation
        </h1>
        <p class="mx-auto max-w-2xl text-lg text-theme-neutral-6">
          How the app connects two devices, and the connection API it is built on.
        </p>
      </header>

      <!-- Table of contents -->
      <Card>
        <CardHeader>
          <CardTitle as="h2">
            Contents
          </CardTitle>
        </CardHeader>
        <CardContent class="pt-0">
          <nav aria-label="Documentation sections">
            <ul class="grid gap-3 sm:grid-cols-2">
              <li v-for="section in sections" :key="section.id">
                <a
                  :href="`#${section.id}`"
                  class="block rounded-lg border border-theme-border p-3 transition-colors hover:border-theme-brand hover:bg-theme-brand/5"
                >
                  <span class="block font-semibold text-theme-fg">{{ section.title }}</span>
                  <span class="block text-sm text-theme-neutral-6">{{ section.summary }}</span>
                </a>
              </li>
            </ul>
          </nav>
        </CardContent>
      </Card>

      <!-- Introduction -->
      <section id="introduction" class="scroll-mt-24">
        <Card>
          <CardHeader>
            <CardTitle as="h2" class="text-3xl">
              Introduction
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-4 pt-0 text-theme-neutral-6">
            <p>
              WebRTC QR Tool connects two browsers peer to peer and uses QR codes instead of a signaling server:
              the WebRTC offer and answer are shown as QR codes and scanned by the other device.
              The engine is the browser's native WebRTC API, wrapped by <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">src/utils/webrtc-native.ts</code>.
              The web app uses no Rust or WebAssembly code.
            </p>

            <h3 class="pt-2 text-xl font-semibold text-theme-fg">
              What it does
            </h3>
            <ul class="list-disc space-y-2 pl-6">
              <li><strong class="text-theme-fg">QR signaling</strong> — the offer and answer are exchanged by QR code or copy-paste; no signaling server is involved.</li>
              <li><strong class="text-theme-fg">Encrypted data channel</strong> — WebRTC always encrypts data channels with DTLS. This is done by the browser, not by app code.</li>
              <li><strong class="text-theme-fg">STUN for NAT traversal</strong> — public STUN servers discover each device's public address. No TURN relay is configured today.</li>
              <li><strong class="text-theme-fg">Offline mode</strong> — when the browser reports it is offline, no ICE servers are used and only local host candidates are offered.</li>
            </ul>

            <h3 class="pt-2 text-xl font-semibold text-theme-fg">
              What it does not do
            </h3>
            <ul class="list-disc space-y-2 pl-6">
              <li>The QR payload is plain JSON and is not encrypted: anyone who can read the QR code can read the SDP, including IP addresses in the ICE candidates.</li>
              <li>Multi-party video calls are not implemented; they need a signaling server.</li>
            </ul>
          </CardContent>
        </Card>
      </section>

      <!-- Getting started -->
      <section id="getting-started" class="scroll-mt-24">
        <Card>
          <CardHeader>
            <CardTitle as="h2" class="text-3xl">
              Getting started
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-6 pt-0">
            <p class="text-theme-neutral-6">
              The connection API is not published as a package. It is the module
              <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">src/utils/webrtc-native.ts</code>
              inside this app, which exports a single manager instance:
            </p>
            <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
              <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                <span />
                <ClipboardButton :text="importSample" variant="ghost" label="Copy" success-message="Copied" />
              </figcaption>
              <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ importSample }}</code></pre>
            </figure>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Connecting two devices
              </h3>
              <p class="text-theme-neutral-6">
                The host creates an offer, the guest turns it into an answer, and the host applies the answer.
                How the strings travel between the devices — QR code, copy-paste — is up to the caller.
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">Host and guest</span>
                  <ClipboardButton :text="connectSample" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ connectSample }}</code></pre>
              </figure>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Knowing when the channel is open
              </h3>
              <p class="text-theme-neutral-6">
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">completeConnection()</code>
                resolves as soon as the answer is applied; that only starts ICE checks.
                The connection is usable when the data channel opens, which the manager announces with a
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">webrtc-state</code> window event.
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">Window events</span>
                  <ClipboardButton :text="eventsSample" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ eventsSample }}</code></pre>
              </figure>
            </div>
          </CardContent>
        </Card>
      </section>

      <!-- Core concepts -->
      <section id="concepts" class="scroll-mt-24">
        <Card>
          <CardHeader>
            <CardTitle as="h2" class="text-3xl">
              Core concepts
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-8 pt-0">
            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                QR payload format
              </h3>
              <p class="text-theme-neutral-6">
                Each QR code carries a JSON string. The host's offer:
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">Offer</span>
                  <ClipboardButton :text="offerFormat" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ offerFormat }}</code></pre>
              </figure>
              <p class="text-theme-neutral-6">
                The guest's answer. <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">offerId</code> is the host's connection id;
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">id</code> is the guest's own.
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">Answer</span>
                  <ClipboardButton :text="answerFormat" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ answerFormat }}</code></pre>
              </figure>
              <p class="text-theme-neutral-6">
                Candidates are gathered before the payload is serialized, so the SDP already contains them
                (no trickle ICE). Gathering waits for end-of-candidates with a 300 ms deadline, then continues
                with the candidates it has; an unreachable STUN server therefore cannot block QR generation.
              </p>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Security
              </h3>
              <ul class="list-disc space-y-2 pl-6 text-theme-neutral-6">
                <li>Data channels are encrypted with DTLS, as the WebRTC specification requires of every browser.</li>
                <li>Each SDP carries its peer's DTLS certificate fingerprint. Because the SDP is exchanged by scanning a code on the other device, a third party would have to alter the QR code itself to insert itself into the connection.</li>
                <li>The QR payload is not encrypted or signed. Treat an offer or answer as readable by anyone who can see the screen.</li>
              </ul>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                ICE servers
              </h3>
              <p class="text-theme-neutral-6">
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">src/utils/ice-config.ts</code> resolves the ICE server list from three layers, in order:
              </p>
              <ol class="space-y-3">
                <li v-for="(layer, index) in iceLayers" :key="layer.name" class="flex gap-3">
                  <Badge variant="secondary" size="lg" class="h-7 shrink-0">{{ index + 1 }}</Badge>
                  <div>
                    <p class="font-medium text-theme-fg">{{ layer.name }}</p>
                    <p class="text-sm text-theme-neutral-6">{{ layer.text }}</p>
                  </div>
                </li>
              </ol>
              <p class="text-theme-neutral-6">
                When <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">navigator.onLine</code> is false the manager uses no ICE servers at all.
                Connection setup never waits on the network: it uses whatever configuration has resolved so far.
              </p>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Connection flow
              </h3>
              <ol class="space-y-4 rounded-lg bg-theme-surface-2 p-6">
                <li v-for="(step, index) in flowSteps" :key="step.title" class="flex gap-4">
                  <Badge variant="primary" size="lg" class="h-7 shrink-0">{{ index + 1 }}</Badge>
                  <div>
                    <p class="font-medium text-theme-fg">{{ step.title }}</p>
                    <p class="text-sm text-theme-neutral-6">{{ step.text }}</p>
                  </div>
                </li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </section>

      <!-- API reference -->
      <section id="api-reference" class="scroll-mt-24">
        <Card>
          <CardHeader>
            <CardTitle as="h2" class="text-3xl">
              API reference
            </CardTitle>
            <CardDescription>
              Methods of <code class="text-theme-fg">webrtcManager</code> (src/utils/webrtc-native.ts).
            </CardDescription>
          </CardHeader>
          <CardContent class="space-y-8 pt-0">
            <div v-for="group in apiGroups" :key="group.title" class="space-y-4">
              <h3 class="text-xl font-semibold text-theme-fg">
                {{ group.title }}
              </h3>
              <dl class="space-y-4">
                <div v-for="entry in group.entries" :key="entry.signature">
                  <dt class="break-words font-mono text-sm font-medium text-theme-fg">
                    {{ entry.signature }}
                  </dt>
                  <dd class="mt-1 text-sm text-theme-neutral-6">
                    {{ entry.text }}
                  </dd>
                </div>
              </dl>
            </div>
          </CardContent>
        </Card>
      </section>

      <!-- Examples -->
      <section id="examples" class="scroll-mt-24">
        <Card>
          <CardHeader>
            <CardTitle as="h2" class="text-3xl">
              Examples
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-8 pt-0">
            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Sending a file
              </h3>
              <p class="text-theme-neutral-6">
                The data channel carries strings, so the File transfer screen sends JSON messages:
                one <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">file-meta</code>, then 64 KiB
                chunks as base64 in <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">file-chunk</code>,
                then <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">file-complete</code>.
                This is the sending side of that protocol (src/views/FileTransfer.vue).
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">File sender</span>
                  <ClipboardButton :text="fileSample" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ fileSample }}</code></pre>
              </figure>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Restarting ICE after a network change
              </h3>
              <p class="text-theme-neutral-6">
                An ICE restart keeps the data channel and DTLS session and replaces only the ICE credentials and candidates.
                Signaling is out of band here too, so the restart offer and answer travel by QR code or paste, like the first exchange.
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">ICE restart</span>
                  <ClipboardButton :text="restartSample" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ restartSample }}</code></pre>
              </figure>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Inspecting the selected path
              </h3>
              <p class="text-theme-neutral-6">
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">getSelectedPath()</code> reads
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">getStats()</code> and reports which candidate pair ICE chose:
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">host</code> (same network),
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">srflx</code> (through NAT, found by STUN) or
                <code class="rounded bg-theme-surface-2 px-1 text-sm text-theme-fg">relay</code> (TURN).
              </p>
              <figure class="overflow-hidden rounded-lg border border-theme-border bg-theme-surface-2">
                <figcaption class="flex items-center justify-between gap-2 border-b border-theme-border px-3 py-1">
                  <span class="text-sm font-medium text-theme-neutral-6">Selected path</span>
                  <ClipboardButton :text="pathSample" variant="ghost" label="Copy" success-message="Copied" />
                </figcaption>
                <pre class="overflow-x-auto p-4 text-sm"><code class="font-mono text-theme-fg">{{ pathSample }}</code></pre>
              </figure>
            </div>
          </CardContent>
        </Card>
      </section>

      <!-- Troubleshooting -->
      <section id="troubleshooting" class="scroll-mt-24">
        <Card>
          <CardHeader>
            <CardTitle as="h2" class="text-3xl">
              Troubleshooting
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-8 pt-0">
            <div class="space-y-4">
              <Alert v-for="issue in issues" :key="issue.title" variant="warning">
                <AlertTitle>{{ issue.title }}</AlertTitle>
                <AlertDescription>
                  <p class="mb-2">{{ issue.symptom }}</p>
                  <ul class="list-disc space-y-1 pl-5">
                    <li v-for="fix in issue.fixes" :key="fix">{{ fix }}</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>

            <div class="space-y-3">
              <h3 class="text-xl font-semibold text-theme-fg">
                Browser requirements
              </h3>
              <p class="text-theme-neutral-6">
                The app needs these browser APIs. Current versions of Chrome, Edge, Firefox and Safari provide them.
              </p>
              <ul class="space-y-3">
                <li v-for="req in requirements" :key="req.api" class="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
                  <Badge :variant="req.required ? 'primary' : 'secondary'" class="w-fit shrink-0">
                    {{ req.required ? 'Required' : 'Optional' }}
                  </Badge>
                  <span>
                    <code class="font-mono text-sm text-theme-fg">{{ req.api }}</code>
                    <span class="text-sm text-theme-neutral-6"> — {{ req.use }}</span>
                  </span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Alert, AlertDescription, AlertTitle } from '@elveris/ui/alert'
import { Badge } from '@elveris/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@elveris/ui/card'
import { ClipboardButton } from '@elveris/ui/clipboard-button'

// Everything documented here is taken from src/utils/webrtc-native.ts,
// src/utils/ice-config.ts and src/views/FileTransfer.vue. If those change,
// this page has to change with them.
//
// Code samples use a plain <pre> plus the kit ClipboardButton rather than the
// kit CodeBlock: CodeBlock.vue declares `codeRef` without reading it, which
// fails this app's vue-tsc gate (TS6133) as soon as the component is imported.

const sections = [
  { id: 'introduction', title: 'Introduction', summary: 'What the app does and does not do' },
  { id: 'getting-started', title: 'Getting started', summary: 'Connecting two devices' },
  { id: 'concepts', title: 'Core concepts', summary: 'Payload format, security, ICE servers' },
  { id: 'api-reference', title: 'API reference', summary: 'Methods and events' },
  { id: 'examples', title: 'Examples', summary: 'File sending, ICE restart, path inspection' },
  { id: 'troubleshooting', title: 'Troubleshooting', summary: 'Common issues and browser requirements' },
]

const importSample = `import { webrtcManager } from '@/utils/webrtc-native'`

const connectSample = `import { webrtcManager } from '@/utils/webrtc-native'

// Host (device A): create the offer and show qrData as a QR code.
const { id: hostId, qrData } = await webrtcManager.createHostConnection(
  'my-app',           // free-form label, carried in the offer as "connection"
  'chat invitation',  // free-form text, carried in the offer as "message"
)

// Guest (device B): scannedOffer is the text read from the host's QR code.
// Show answerData as a QR code.
const { id: guestId, answerData } = await webrtcManager.acceptConnection(scannedOffer)

// Host (device A): scannedAnswer is the text read from the guest's QR code.
await webrtcManager.completeConnection(hostId, scannedAnswer)`

const eventsSample = `// Data channel state: 'open' | 'closed' | 'error'
window.addEventListener('webrtc-state', (event) => {
  const { connectionId, state } = (event as CustomEvent).detail
  if (state === 'open') {
    // sendMessage returns false when the channel is not open.
    webrtcManager.sendMessage(connectionId, 'hello')
  }
})

// Incoming data channel messages
window.addEventListener('webrtc-message', (event) => {
  const { connectionId, message } = (event as CustomEvent).detail
  console.log(connectionId, message)
})`

const offerFormat = `{
  "id": "conn_…",
  "type": "webrtc-qr-connection",
  "connection": "my-app",
  "message": "chat invitation",
  "timestamp": "<ISO 8601 time>",
  "offer": { "type": "offer", "sdp": "v=0…" }
}`

const answerFormat = `{
  "type": "answer",
  "id": "conn_…",
  "offerId": "conn_…",
  "sdp": "v=0…"
}`

const iceLayers = [
  {
    name: 'Static',
    text: 'Built into the app and always present: stun:stun.elveris.work:443 (Elveris, us-ashburn-1) and stun:stun.cloudflare.com:3478 (fallback).',
  },
  {
    name: 'Object storage',
    text: 'If VITE_ICE_CONFIG_URL is set, a JSON document at that URL replaces the static STUN list. TURN URLs in it are rejected.',
  },
  {
    name: 'Server grant',
    text: 'The only source of TURN credentials: a short-lived ConnectivityGrant from the control plane. This app registers no grant fetcher, so it currently has no TURN.',
  },
]

const flowSteps = [
  { title: 'Host creates an offer', text: 'A peer connection and a "messages" data channel are created; the local offer is set.' },
  { title: 'Host gathers ICE candidates', text: 'Up to 300 ms, then the offer with its candidates is serialized as JSON.' },
  { title: 'Guest applies the offer', text: 'The guest scans the QR code, sets the remote description and creates an answer.' },
  { title: 'Guest shows the answer', text: 'After its own candidate gathering, the answer is shown as a QR code.' },
  { title: 'Host applies the answer', text: 'ICE connectivity checks start between the two devices.' },
  { title: 'Data channel opens', text: 'Both sides receive a webrtc-state event with state "open"; only now is the connection usable.' },
]

const apiGroups = [
  {
    title: 'Connection setup',
    entries: [
      {
        signature: 'createHostConnection(connectionData: string, message: string): Promise<{ id: string; qrData: string }>',
        text: 'Creates a peer connection with a data channel, waits for ICE gathering and returns the offer payload to show as a QR code.',
      },
      {
        signature: 'acceptConnection(qrDataString: string): Promise<{ id: string; answerData: string }>',
        text: 'Guest side: applies an offer payload and returns the answer payload. The guest gets its own connection id.',
      },
      {
        signature: 'completeConnection(connectionId: string, answerData: string): Promise<void>',
        text: 'Host side: applies an answer payload (also the answer to an ICE restart). Resolves when the answer is applied, not when the channel opens.',
      },
    ],
  },
  {
    title: 'Messaging and lifecycle',
    entries: [
      {
        signature: 'sendMessage(connectionId: string, message: string | ArrayBuffer | ArrayBufferView): boolean',
        text: 'Sends a string or raw binary buffer over the data channel. Returns false if the connection is unknown or the channel is not open.',
      },
      {
        signature: 'sendBinary(connectionId: string, data: ArrayBuffer | ArrayBufferView): boolean',
        text: 'Sends a raw binary buffer over the data channel directly without Base64 overhead.',
      },
      {
        signature: 'getConnectionState(connectionId: string): string',
        text: '"connecting", "connected", "disconnected" or "error". Unknown ids report "disconnected".',
      },
      {
        signature: 'closeConnection(connectionId: string): void',
        text: 'Closes the data channel and the peer connection and forgets the connection.',
      },
      {
        signature: 'getActiveConnections(): string[]',
        text: 'Ids of the connections the manager currently holds.',
      },
    ],
  },
  {
    title: 'ICE and diagnostics',
    entries: [
      {
        signature: 'restartIce(connectionId: string): Promise<string>',
        text: 'Starts an ICE restart; requires signaling state "stable". Returns a restart offer for the peer.',
      },
      {
        signature: 'acceptIceRestart(connectionId: string, restartData: string): Promise<string>',
        text: 'Peer side of restartIce(): applies the restart offer and returns an answer, which goes back through completeConnection().',
      },
      {
        signature: 'setIceConfiguration(connectionId: string, config: { iceServers?, iceTransportPolicy? }): void',
        text: 'Replaces the ICE servers or transport policy of a running connection. Takes effect for candidates gathered after the next restartIce().',
      },
      {
        signature: 'getSelectedPath(connectionId: string): Promise<SelectedPath | null>',
        text: 'The candidate pair ICE selected (candidate types, protocol, addresses, round-trip time, bytes), or null while none is selected.',
      },
      {
        signature: 'getIceCandidateErrors(connectionId: string): IceCandidateErrorRecord[]',
        text: 'Candidate-gathering errors reported by the browser, for example an unreachable STUN server.',
      },
      {
        signature: 'getLastIceConfig(): ResolvedIceConfig | null',
        text: 'The ICE configuration (sources, layer outcomes, configHash) the most recent connection used; null in offline mode.',
      },
    ],
  },
  {
    title: 'Window events',
    entries: [
      {
        signature: "'webrtc-state'  detail: { connectionId, state: 'open' | 'closed' | 'error' }",
        text: 'Data channel state changes.',
      },
      {
        signature: "'webrtc-message'  detail: { connectionId, message, isBinary? }",
        text: 'A message arrived on the data channel.',
      },
      {
        signature: "'webrtc-binary'  detail: { connectionId, data: ArrayBuffer }",
        text: 'A raw binary packet arrived on the data channel (e.g. on-the-fly file chunk).',
      },
      {
        signature: "'webrtc-ice-state'  detail: { connectionId, iceConnectionState, connectionState }",
        text: 'ICE and peer connection state changes.',
      },
      {
        signature: "'webrtc-ice-error'  detail: IceCandidateErrorRecord",
        text: 'A candidate-gathering error (the same records getIceCandidateErrors() returns).',
      },
    ],
  },
]

const fileSample = `import { webrtcManager } from '@/utils/webrtc-native'

const CHUNK_SIZE = 64 * 1024

function send(connectionId: string, payload: unknown) {
  if (!webrtcManager.sendMessage(connectionId, JSON.stringify(payload))) {
    throw new Error('Data channel is not open; the payload was not sent')
  }
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(binary)
}

async function sendFile(connectionId: string, file: File) {
  const id = crypto.randomUUID()
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE))

  send(connectionId, { kind: 'file-meta', id, name: file.name, size: file.size, mimeType: file.type, totalChunks })

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * CHUNK_SIZE
    const buffer = await file.slice(start, start + CHUNK_SIZE).arrayBuffer()
    send(connectionId, { kind: 'file-chunk', id, index, totalChunks, data: toBase64(buffer) })
  }

  send(connectionId, { kind: 'file-complete', id })
}`

const restartSample = `// Side A (either peer; signaling state must be 'stable')
const restartOffer = await webrtcManager.restartIce(idA)
// ...deliver restartOffer to side B by QR code or paste...

// Side B
const restartAnswer = await webrtcManager.acceptIceRestart(idB, restartOffer)
// ...deliver restartAnswer back to side A...

// Side A
await webrtcManager.completeConnection(idA, restartAnswer)`

const pathSample = `const path = await webrtcManager.getSelectedPath(connectionId)
if (path) {
  console.log(path.localCandidateType, '->', path.remoteCandidateType)  // e.g. 'srflx' -> 'host'
  console.log(path.protocol, path.currentRoundTripTime)                  // 'udp', seconds
} else {
  console.log('ICE has not selected a candidate pair yet')
}`

const issues = [
  {
    title: 'Camera not working',
    symptom: 'The app cannot access the camera.',
    fixes: [
      'Open the app over HTTPS (or on localhost): browsers only allow camera access in a secure context.',
      'Grant camera permission when prompted, or re-enable it in the browser\'s site settings.',
      'No camera available? Use the paste option to exchange the offer and answer as text.',
    ],
  },
  {
    title: 'Connection does not open',
    symptom: 'The answer was applied but the data channel never opens.',
    fixes: [
      'Check both devices have network access; offline, they must be on the same local network.',
      'Listen for webrtc-ice-error or call getIceCandidateErrors() to see whether the STUN servers are reachable.',
      'Firewalls that block UDP and symmetric NATs usually need a TURN relay. The app has none configured; setIceConfiguration() followed by restartIce() applies one you supply to a running connection.',
    ],
  },
  {
    title: 'QR code not scanning',
    symptom: 'The camera does not detect the QR code.',
    fixes: [
      'Improve the lighting and hold the camera steady at a distance where the whole code is in view.',
      'Increase the screen brightness of the device showing the code.',
      'The offer contains the full SDP, so the code is dense; if it will not scan, use the paste option.',
    ],
  },
]

const requirements = [
  { api: 'RTCPeerConnection, RTCDataChannel', required: true, use: 'the connection and the message channel' },
  { api: 'navigator.mediaDevices.getUserMedia', required: false, use: 'scanning QR codes with the camera (paste works without it)' },
  { api: 'RTCPeerConnection.restartIce()', required: false, use: 'ICE restart only' },
  { api: 'RTCPeerConnection.getStats()', required: false, use: 'getSelectedPath() diagnostics only' },
]
</script>
